import React from 'react';
import PropTypes from 'prop-types';
import { Snackbar } from '@mui/material';
import TagDialog from '../../../organisms/TagDialog';
import AddToCollectionDialog from '../AddToCollectionDialog';
import SimilarArticlesDialog from '../SimilarArticlesDialog';
import ArticleShareMenu from '../../../molecules/ArticleShareMenu';
import { ArticleExposureMenu, ArticleSentimentMenu } from './ArticleAnalysisMenus';
import ArticlePrintFullscreenDialog from './ArticlePrintFullscreenDialog';

const ArticleModalAuxiliaryDialogs = ({
  article,
  availableTags,
  collectionDialogOpen,
  exposureAnchor,
  exposureAnalyzing,
  fullscreenOpen,
  fullscreenPageIndex,
  fullscreenZoom,
  lastAddedTag,
  rootArticleId,
  sentimentAnchor,
  sentimentAnalyzing,
  shareMenuAnchor,
  similarDialogOpen,
  snackbarMessage,
  snackbarOpen,
  tagDialogOpen,
  onAddTags,
  onAnalyzeExposure,
  onAnalyzeSentiment,
  onCloseCollectionDialog,
  onCloseExposure,
  onCloseFullscreen,
  onCloseSentiment,
  onCloseShare,
  onCloseSimilarDialog,
  onCloseSnackbar,
  onCloseTagDialog,
  onCollectionSuccess,
  onCreateNewsletter,
  onNotify,
  onSelectExposure,
  onSelectSentiment,
  onSimilarSuccess,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  readOnly
}) => (
  <>
    {!readOnly && (
      <>
        <ArticleSentimentMenu
          anchorEl={sentimentAnchor}
          currentValue={article.sentiment}
          analyzing={sentimentAnalyzing}
          onClose={onCloseSentiment}
          onSelect={onSelectSentiment}
          onAnalyze={onAnalyzeSentiment}
        />

        <ArticleExposureMenu
          anchorEl={exposureAnchor}
          currentValue={article.exposure}
          analyzing={exposureAnalyzing}
          onClose={onCloseExposure}
          onSelect={onSelectExposure}
          onAnalyze={onAnalyzeExposure}
        />
      </>
    )}

    <ArticleShareMenu
      anchorEl={shareMenuAnchor}
      article={article}
      originalUrl={article.siteUrl || article.url || article.videoUrl}
      onClose={onCloseShare}
      onCreateNewsletter={onCreateNewsletter}
      onNotify={onNotify}
      showEmail={false}
      showNewsletter={!readOnly}
    />

    <Snackbar
      open={snackbarOpen}
      autoHideDuration={3000}
      onClose={onCloseSnackbar}
      message={snackbarMessage}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    />

    {!readOnly && (
      <>
        <TagDialog
          open={tagDialogOpen}
          onClose={onCloseTagDialog}
          onAddTags={onAddTags}
          availableTags={availableTags}
          mode="both"
        />

        <AddToCollectionDialog
          open={collectionDialogOpen}
          onClose={onCloseCollectionDialog}
          articleIds={rootArticleId ? [rootArticleId] : []}
          onSuccess={onCollectionSuccess}
        />

        <SimilarArticlesDialog
          open={similarDialogOpen}
          onClose={onCloseSimilarDialog}
          articleId={rootArticleId}
          tagId={lastAddedTag?._id}
          tagName={lastAddedTag?.name}
          onSuccess={onSimilarSuccess}
        />
      </>
    )}

    <ArticlePrintFullscreenDialog
      article={article}
      open={fullscreenOpen}
      initialPageIndex={fullscreenPageIndex}
      zoom={fullscreenZoom}
      onClose={onCloseFullscreen}
      onZoomIn={onZoomIn}
      onZoomOut={onZoomOut}
      onZoomReset={onZoomReset}
    />
  </>
);

ArticleModalAuxiliaryDialogs.propTypes = {
  article: PropTypes.shape({
    _id: PropTypes.string,
    exposure: PropTypes.string,
    sentiment: PropTypes.string,
    siteUrl: PropTypes.string,
    url: PropTypes.string,
    videoUrl: PropTypes.string
  }).isRequired,
  availableTags: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    color: PropTypes.string,
    name: PropTypes.string
  })),
  collectionDialogOpen: PropTypes.bool.isRequired,
  exposureAnchor: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  exposureAnalyzing: PropTypes.bool.isRequired,
  fullscreenOpen: PropTypes.bool.isRequired,
  fullscreenPageIndex: PropTypes.number.isRequired,
  fullscreenZoom: PropTypes.number.isRequired,
  lastAddedTag: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string
  }),
  rootArticleId: PropTypes.string,
  sentimentAnchor: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  sentimentAnalyzing: PropTypes.bool.isRequired,
  shareMenuAnchor: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  similarDialogOpen: PropTypes.bool.isRequired,
  snackbarMessage: PropTypes.string.isRequired,
  snackbarOpen: PropTypes.bool.isRequired,
  tagDialogOpen: PropTypes.bool.isRequired,
  onAddTags: PropTypes.func.isRequired,
  onAnalyzeExposure: PropTypes.func.isRequired,
  onAnalyzeSentiment: PropTypes.func.isRequired,
  onCloseCollectionDialog: PropTypes.func.isRequired,
  onCloseExposure: PropTypes.func.isRequired,
  onCloseFullscreen: PropTypes.func.isRequired,
  onCloseSentiment: PropTypes.func.isRequired,
  onCloseShare: PropTypes.func.isRequired,
  onCloseSimilarDialog: PropTypes.func.isRequired,
  onCloseSnackbar: PropTypes.func.isRequired,
  onCloseTagDialog: PropTypes.func.isRequired,
  onCollectionSuccess: PropTypes.func.isRequired,
  onCreateNewsletter: PropTypes.func.isRequired,
  onNotify: PropTypes.func.isRequired,
  onSelectExposure: PropTypes.func.isRequired,
  onSelectSentiment: PropTypes.func.isRequired,
  onSimilarSuccess: PropTypes.func.isRequired,
  onZoomIn: PropTypes.func.isRequired,
  onZoomOut: PropTypes.func.isRequired,
  onZoomReset: PropTypes.func.isRequired,
  readOnly: PropTypes.bool
};

ArticleModalAuxiliaryDialogs.defaultProps = {
  availableTags: [],
  exposureAnchor: null,
  lastAddedTag: null,
  rootArticleId: undefined,
  sentimentAnchor: null,
  shareMenuAnchor: null,
  readOnly: false
};

export default ArticleModalAuxiliaryDialogs;

